<template>
    <div class="row">
        <!-- Quick Run Panel -->
        <div class="col-12">
            <RunNow @refresh="refreshAll" />
        </div>

        <!-- Job List or Editor -->
        <div class="col-12">
            <SchedulerJob 
                v-if="showEditor"
                :job="selectedJob"
                @saved="onJobSaved"
                @cancel="closeEditor"
            />
            
            <SchedulerList 
                v-else
                :jobs="jobs"
                :isLoaded="isLoaded"
                @create="openCreateForm"
                @edit="openEditForm"
                @refresh="refreshAll"
            />
        </div>

        <!-- Supplier Schedules Info -->
        <div class="col-12 mt-3">
            <div class="card"
                :class="{
                    'card-success': hasActiveSupplierSchedules,
                    'card-outline card-secondary collapsed-card': !hasActiveSupplierSchedules
                }"
            >
                <div class="card-header">
                    <h3 class="card-title">
                        <i class="fas fa-info-circle"></i> Active Supplier Schedules
                    </h3>
                    <div class="card-tools">
                        <button type="button" class="btn btn-tool" data-card-widget="collapse">
                            <i class="fas" :class="hasActiveSupplierSchedules ? 'fa-minus' : 'fa-plus'"></i>
                        </button>
                    </div>
                </div>
                <div class="card-body">
                    <p class="text-muted">
                        These schedules are automatically created based on each supplier's configured schedule time.
                    </p>
                    <div v-if="supplierSchedules.length > 0">
                        <table class="table table-sm table-striped">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Supplier Name</th>
                                    <th>Schedule Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="schedule in supplierSchedules" :key="schedule.id">
                                    <td>{{ schedule.id }}</td>
                                    <td>{{ schedule.name }}</td>
                                    <td>{{ schedule.schedule }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="text-center text-muted py-3">
                        <i class="fas fa-calendar-times"></i> No supplier schedules configured
                    </div>
                </div>
            </div>
        </div>

        <!-- Execution History -->
        <div class="col-12 mt-3">
            <div class="card card-outline card-info">
                <div class="card-header">
                    <h3 class="card-title">
                        <i class="fas fa-history"></i> Scheduler History
                    </h3>
                    <div class="card-tools">
                        <button class="btn btn-tool text-danger" @click="clearHistory" title="Clear history">
                            <i class="fas fa-trash"></i>
                        </button>
                        <button class="btn btn-tool" @click="loadHistory" :disabled="loadingHistory">
                            <i class="fas fa-sync" :class="{'fa-spin': loadingHistory}"></i>
                        </button>
                    </div>
                </div>
                <div class="card-body table-responsive p-0">
                    <table class="table table-sm table-striped mb-0">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Source</th>
                                <th>Operation</th>
                                <th>Target</th>
                                <th>Date</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in history" :key="item.id">
                                <td>{{ formatDate(item.finished_at || item.started_at) }}</td>
                                <td>
                                    <span class="badge badge-light">{{ item.request_source || item.source || '-' }}</span>
                                </td>
                                <td>{{ item.operation_type || '-' }}</td>
                                <td>{{ item.target_type || '-' }}<span v-if="item.target_ids && item.target_ids.length"> #{{ item.target_ids[0] }}</span></td>
                                <td>{{ item.date || '-' }}</td>
                                <td>
                                    <span class="badge" :class="statusClass(item.status)">{{ item.status || '-' }}</span>
                                </td>
                            </tr>
                            <tr v-if="history.length === 0 && !loadingHistory">
                                <td colspan="6" class="text-center text-muted py-3">
                                    No scheduler history yet
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="card-footer clearfix" v-if="historyTotal > historyLimit">
                    <div class="float-left text-muted pt-1">
                        Showing {{ historyOffset + 1 }}-{{ Math.min(historyOffset + historyLimit, historyTotal) }} of {{ historyTotal }}
                    </div>
                    <ul class="pagination pagination-sm m-0 float-right">
                        <li class="page-item" :class="{ disabled: historyOffset === 0 }">
                            <a class="page-link" href="#" @click.prevent="historyPrev">Previous</a>
                        </li>
                        <li class="page-item">
                            <a class="page-link">{{ historyPage }} / {{ historyPageCount }}</a>
                        </li>
                        <li class="page-item" :class="{ disabled: historyOffset + historyLimit >= historyTotal }">
                            <a class="page-link" href="#" @click.prevent="historyNext">Next</a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SchedulerList from '@/components/scheduler/List.vue'
import SchedulerJob from '@/components/scheduler/Job.vue'
import RunNow from '@/components/scheduler/RunNow.vue'

import { RepositoryFactory } from '@/services/repositories/ManagementAPI/RepositoryFactory'
const SchedulerRepo = RepositoryFactory.get('scheduler')

export default {
    name: 'Scheduler',
    components: {
        SchedulerList,
        SchedulerJob,
        RunNow
    },
    data() {
        return {
            jobs: [],
            supplierSchedules: [],
            history: [],
            historyTotal: 0,
            historyLimit: 50,
            historyOffset: 0,
            selectedJob: null,
            showEditor: false,
            isLoaded: false,
            loadingHistory: false
        }
    },
    computed: {
        hasActiveSupplierSchedules() {
            return this.supplierSchedules.length > 0
        },
        historyPage() {
            return Math.floor(this.historyOffset / this.historyLimit) + 1
        },
        historyPageCount() {
            return Math.ceil(this.historyTotal / this.historyLimit) || 1
        }
    },
    created() {
        this.refreshAll()
    },
    methods: {
        async refreshAll() {
            await Promise.all([
                this.loadJobs(),
                this.loadSupplierSchedules(),
                this.loadHistory()
            ])
        },
        async loadJobs() {
            this.isLoaded = false
            try {
                const response = await SchedulerRepo.list()
                this.jobs = response.data.results || []
            } catch (error) {
                this.$notify({ group: 'ops', type: 'error', title: 'Error', text: 'Failed to load scheduled jobs' })
                console.error(error)
                this.jobs = []
            } finally {
                this.isLoaded = true
            }
        },
        async loadSupplierSchedules() {
            try {
                const response = await SchedulerRepo.getSchedules()
                this.supplierSchedules = response.data.schedules || []
            } catch (error) {
                console.error('Failed to load supplier schedules:', error)
            }
        },
        async loadHistory() {
            this.loadingHistory = true
            try {
                const response = await SchedulerRepo.history({ limit: this.historyLimit, offset: this.historyOffset })
                this.history = response.data.results || []
                this.historyTotal = response.data.count || 0
            } catch (error) {
                console.error('Failed to load scheduler history:', error)
                this.history = []
                this.historyTotal = 0
            } finally {
                this.loadingHistory = false
            }
        },
        historyNext() {
            if (this.historyOffset + this.historyLimit < this.historyTotal) {
                this.historyOffset += this.historyLimit
                this.loadHistory()
            }
        },
        historyPrev() {
            if (this.historyOffset > 0) {
                this.historyOffset = Math.max(0, this.historyOffset - this.historyLimit)
                this.loadHistory()
            }
        },
        async clearHistory() {
            this.$swal({
                title: 'Clear History?',
                text: 'This will delete all scheduler history records.',
                icon: 'warning',
                buttons: ['Cancel', 'Clear'],
                dangerMode: true
            }).then(async (confirm) => {
                if (confirm) {
                    try {
                        await SchedulerRepo.clearHistory()
                        this.historyOffset = 0
                        await this.loadHistory()
                    } catch (error) {
                        console.error('Failed to clear history:', error)
                    }
                }
            })
        },
        formatDate(dateStr) {
            if (!dateStr) {
                return '-'
            }
            const date = new Date(dateStr)
            return date.toLocaleString()
        },
        statusClass(status) {
            const classes = {
                success: 'badge-success',
                failed: 'badge-danger',
                error: 'badge-danger',
                partial: 'badge-warning',
                running: 'badge-info'
            }
            return classes[status] || 'badge-secondary'
        },
        openCreateForm() {
            this.selectedJob = null
            this.showEditor = true
        },
        openEditForm(job) {
            this.selectedJob = job
            this.showEditor = true
        },
        closeEditor() {
            this.selectedJob = null
            this.showEditor = false
        },
        onJobSaved(data) {
            this.closeEditor()
            this.refreshAll()
        }
    }
}
</script>

<style scoped>
.collapsed-card .card-body {
    display: none;
}
</style>
